import React, { useState, useEffect, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  X, 
  Shirt,
  Package,
  Truck,
  CreditCard,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/shop`;

// Cart Context
const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('mimCart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('mimCart', JSON.stringify(items));
  }, [items]);

  const addToCart = (product, variant) => {
    setItems(prev => {
      const existing = prev.find(item => item.sync_variant_id === variant.id);
      if (existing) {
        return prev.map(item =>
          item.sync_variant_id === variant.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      
      // Get the mockup/preview image (not the design file)
      let thumbnailUrl = product.thumbnail_url;
      if (variant.files) {
        const previewFile = variant.files.find(f => f.type === 'preview');
        if (previewFile?.preview_url) {
          thumbnailUrl = previewFile.preview_url;
        } else if (variant.product?.image) {
          thumbnailUrl = variant.product.image;
        }
      }
      
      return [...prev, {
        sync_variant_id: variant.id,
        name: product.name,
        variant_name: variant.name,
        price: parseFloat(variant.retail_price),
        quantity: 1,
        thumbnail: thumbnailUrl
      }];
    });
    toast.success('Added to cart!');
  };

  const removeFromCart = (variantId) => {
    setItems(prev => prev.filter(item => item.sync_variant_id !== variantId));
  };

  const updateQuantity = (variantId, quantity) => {
    if (quantity < 1) return;
    setItems(prev =>
      prev.map(item =>
        item.sync_variant_id === variantId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      total,
      itemCount,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Product Card Component
const ProductCard = ({ product, onViewDetails }) => {
  return (
    <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
      <div className="relative aspect-square bg-gray-100 overflow-hidden">
        {product.thumbnail_url ? (
          <img
            src={product.thumbnail_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Shirt className="w-16 h-16 text-gray-300" />
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
        <p className="text-sm text-gray-500 mb-3">{product.variants} variants available</p>
        <Button 
          size="sm" 
          onClick={() => onViewDetails(product)}
          className="w-full bg-red-600 hover:bg-red-700"
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );
};

// Product Detail Modal
const ProductDetailModal = ({ product, onClose, onViewCart, onCheckout }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    if (product?.sync_variants?.length > 0) {
      setSelectedVariant(product.sync_variants[0]);
    }
    // Reset addedToCart when product changes
    setAddedToCart(false);
  }, [product]);

  if (!product) return null;

  const handleAddToCart = () => {
    if (selectedVariant) {
      addToCart(product.sync_product, selectedVariant);
      setAddedToCart(true);
    }
  };

  // Get the mockup/preview image (not the design file)
  const getProductImage = () => {
    if (selectedVariant?.files) {
      // First try to find 'preview' type file (mockup)
      const previewFile = selectedVariant.files.find(f => f.type === 'preview');
      if (previewFile?.preview_url) return previewFile.preview_url;
      
      // Fallback to thumbnail_url which is usually the mockup
      if (selectedVariant.product?.image) return selectedVariant.product.image;
    }
    // Fallback to product thumbnail
    return product.sync_product?.thumbnail_url;
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold">{product.sync_product?.name}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 p-6">
          <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
            {getProductImage() ? (
              <img
                src={getProductImage()}
                alt={product.sync_product?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Shirt className="w-24 h-24 text-gray-300" />
              </div>
            )}
          </div>
          
          <div>
            <div className="mb-6">
              <span className="text-3xl font-bold text-red-600">
                ${selectedVariant?.retail_price || 'N/A'}
              </span>
            </div>
            
            {product.sync_variants?.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Size/Style
                </label>
                <select
                  value={selectedVariant?.id || ''}
                  onChange={(e) => {
                    const variant = product.sync_variants.find(v => v.id === parseInt(e.target.value));
                    setSelectedVariant(variant);
                    setAddedToCart(false); // Reset when variant changes
                  }}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-red-500 focus:border-red-500 cursor-pointer"
                >
                  {product.sync_variants.map((variant) => (
                    <option key={variant.id} value={variant.id} className="text-gray-900 bg-white py-2">
                      {variant.name} - ${variant.retail_price}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {!addedToCart ? (
              <Button 
                onClick={handleAddToCart}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
                disabled={!selectedVariant}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
            ) : (
              <div className="space-y-3">
                {/* Success Message */}
                <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">Added to cart!</span>
                </div>
                
                {/* Action Buttons */}
                <div className="grid grid-cols-1 gap-2">
                  <Button 
                    onClick={() => {
                      onClose();
                      onCheckout();
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    Checkout
                  </Button>
                  
                  <Button 
                    onClick={() => {
                      onClose();
                      onViewCart();
                    }}
                    variant="outline"
                    className="w-full border-2 border-gray-900 text-gray-900 hover:bg-gray-100 py-3"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    View Cart
                  </Button>
                  
                  <Button 
                    onClick={onClose}
                    variant="ghost"
                    className="w-full text-gray-600 hover:text-gray-900 hover:bg-gray-50 py-3"
                  >
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Continue Shopping
                  </Button>
                </div>
              </div>
            )}
            
            <div className="mt-6 space-y-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                <span>Print-on-demand - Made just for you</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4" />
                <span>Ships within 2-7 business days</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shopping Cart Sidebar
const CartSidebar = ({ isOpen, onClose, autoOpenCheckout, onCheckoutOpened }) => {
  const { items, total, removeFromCart, updateQuantity, clearCart } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Handle auto-open checkout when coming from product modal
  useEffect(() => {
    if (autoOpenCheckout && items.length > 0) {
      setIsCheckingOut(true);
      if (onCheckoutOpened) onCheckoutOpened();
    }
  }, [autoOpenCheckout, items.length, onCheckoutOpened]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ShoppingCart className="w-6 h-6" />
            Your Cart
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.sync_variant_id} className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                  {item.thumbnail && (
                    <img
                      src={item.thumbnail}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.variant_name}</p>
                    <p className="text-red-600 font-bold mt-1">${item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.sync_variant_id, item.quantity - 1)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.sync_variant_id, item.quantity + 1)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.sync_variant_id)}
                        className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {items.length > 0 && (
          <div className="p-4 border-t">
            <div className="flex justify-between text-lg font-bold mb-4">
              <span>Total:</span>
              <span className="text-red-600">${total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Shipping calculated at checkout
            </p>
            <Button
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              onClick={() => setIsCheckingOut(true)}
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Proceed to Checkout
            </Button>
            <button
              onClick={clearCart}
              className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Clear Cart
            </button>
          </div>
        )}
      </div>
      
      {isCheckingOut && (
        <CheckoutModal onClose={() => setIsCheckingOut(false)} />
      )}
    </div>
  );
};

// Checkout Modal - Stripe Integration
const CheckoutModal = ({ onClose }) => {
  const { items, total, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  const handleStripeCheckout = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);

    try {
      const checkoutPayload = {
        items: items.map(item => ({
          sync_variant_id: item.sync_variant_id,
          quantity: item.quantity,
          name: item.name,
          price: item.price,
          thumbnail: item.thumbnail
        })),
        customer_email: email,
        origin_url: window.location.origin
      };

      const response = await axios.post(`${BACKEND_URL}/api/payments/checkout/session`, checkoutPayload);
      
      if (response.data.url) {
        // Clear cart before redirecting
        clearCart();
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-md w-full overflow-hidden">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Secure Checkout</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6">
          {/* Order Summary */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-3">Order Summary</h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {items.map((item) => (
                <div key={item.sync_variant_id} className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {item.name} ({item.variant_name}) x{item.quantity}
                  </span>
                  <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t mt-3 pt-3 flex justify-between font-bold">
              <span>Total:</span>
              <span className="text-red-600">${total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Shipping will be calculated at checkout</p>
          </div>
          
          {/* Email Form */}
          <form onSubmit={handleStripeCheckout} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                We'll send your order confirmation here
              </p>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  Redirecting to Payment...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Pay with Stripe
                </span>
              )}
            </Button>
          </form>
          
          {/* Security Note */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 text-center">
              🔒 Secure payment powered by Stripe. Your card details are never stored on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Shop Page Component
const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const { itemCount } = useCart();

  useEffect(() => {
    fetchProducts();
    
    // Check for payment status in URL
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (payment === 'success') {
      setPaymentStatus('success');
      // Clear URL params
      window.history.replaceState({}, '', '/store');
      // Optionally verify the session
      if (sessionId) {
        verifyPayment(sessionId);
      }
    } else if (payment === 'cancelled') {
      setPaymentStatus('cancelled');
      window.history.replaceState({}, '', '/store');
    }
  }, []);

  const handleViewCart = () => {
    setCartOpen(true);
  };

  const handleCheckout = () => {
    setCartOpen(true);
    // Small delay to ensure cart opens first, then trigger checkout
    setTimeout(() => setIsCheckingOut(true), 100);
  };

  const verifyPayment = async (sessionId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/checkout/status/${sessionId}`);
      console.log('Payment verified:', response.data);
    } catch (err) {
      console.error('Payment verification error:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (product) => {
    try {
      const response = await axios.get(`${API}/products/${product.id}`);
      setSelectedProduct(response.data.product);
    } catch (err) {
      console.error('Error fetching product details:', err);
      toast.error('Failed to load product details');
    }
  };

  return (
    <div>
      <SEO 
        title="Shop MiM Apparel - Support Youth Programs"
        description="Shop Mentors in Motion apparel and merchandise. Every purchase supports youth tutoring, mentoring, and creative learning programs in St. Louis."
        keywords="mentors in motion merchandise, nonprofit apparel, support youth programs, MiM clothing, St. Louis nonprofit shop"
        canonicalUrl="/store"
      />
      
      {/* Payment Status Banner */}
      {paymentStatus === 'success' && (
        <div className="bg-green-50 border-b border-green-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-green-900">Payment Successful!</h3>
                  <p className="text-sm text-green-700">Thank you for your purchase. You'll receive an email confirmation shortly.</p>
                </div>
              </div>
              <button 
                onClick={() => setPaymentStatus(null)}
                className="text-green-600 hover:text-green-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {paymentStatus === 'cancelled' && (
        <div className="bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-yellow-900">Payment Cancelled</h3>
                  <p className="text-sm text-yellow-700">Your payment was cancelled. Your cart items have been saved.</p>
                </div>
              </div>
              <button 
                onClick={() => setPaymentStatus(null)}
                className="text-yellow-600 hover:text-yellow-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(227,27,35,0.3),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-start">
            <div className="max-w-2xl">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                Apparel Store
              </h1>
              <p className="text-base text-gray-300 leading-relaxed">
                Show your support for Mentors in Motion with our official apparel. Every purchase helps fund our programs and supports youth in the St. Louis community.
              </p>
            </div>
            
            <button
              onClick={() => setCartOpen(true)}
              className="relative bg-red-600 hover:bg-red-700 text-white p-3 rounded-full transition-colors"
            >
              <ShoppingCart className="w-6 h-6" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-600">Loading products...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600">{error}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
              <Shirt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Coming Soon!</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                We're currently setting up our apparel store. Check back soon for official Mentors in Motion merchandise including t-shirts, hoodies, hats, and more!
              </p>
              <div className="bg-gray-50 p-6 rounded-xl max-w-lg mx-auto text-left">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5 text-red-600" />
                  What to Expect
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                    Official MiM branded t-shirts
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                    Comfortable hoodies and sweatshirts
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                    Stylish caps and hats
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                    Professional polo shirts
                  </li>
                </ul>
              </div>
              <Link to="/donate">
                <Button className="mt-6 bg-red-600 hover:bg-red-700 text-white">
                  Support MiM Today
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Quality Products</h3>
              <p className="text-gray-600 text-sm">
                All items are printed on premium quality materials for comfort and durability.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Fast Shipping</h3>
              <p className="text-gray-600 text-sm">
                Items ship within 2-7 business days directly to your door.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Support Our Mission</h3>
              <p className="text-gray-600 text-sm">
                Every purchase helps fund programs for youth in St. Louis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onViewCart={handleViewCart}
          onCheckout={handleCheckout}
        />
      )}

      {/* Cart Sidebar */}
      <CartSidebar 
        isOpen={cartOpen} 
        onClose={() => {
          setCartOpen(false);
          setIsCheckingOut(false);
        }}
        autoOpenCheckout={isCheckingOut}
        onCheckoutOpened={() => setIsCheckingOut(false)}
      />
    </div>
  );
};

// Wrapped Shop Component with Cart Provider
const ShopWithCart = () => (
  <CartProvider>
    <Shop />
  </CartProvider>
);

export default ShopWithCart;
